// Shared grouping for the foldable-batch view used on /approvals and
// /payouts. Single source of truth so the two pages can't drift —
// ordering, orphan handling, and total computation all live here.
//
// Why grouping matters: a single CSV upload reserves up to 500 rows
// that share a batch_id. Rendering them as one flat list buries the
// per-batch identity and forces operators to mentally re-aggregate.
// Grouping by batch_id + collapsing by default keeps the page
// scannable when there are 5+ pending batches.
//
// CLAUDE.md rule 6: amount_minor is bigint end-to-end; sums here stay
// bigint and never go through Number().
import type { Payout } from "@/api/payouts";

/**
 * PayoutGroup — one row-grouping in either queue.
 *
 * Batched groups carry a numeric batchId and identify a CSV upload;
 * orphans (batch_id=null) collect rows that came in via the single-
 * payout endpoint / M2M API and don't share a parent batch.
 */
export interface PayoutGroup {
  /** Stable React key: "batch-<id>" for batched groups, "orphans" for nulls. */
  key: string;
  /** Numeric batch id; null for the orphan group. */
  batchId: number | null;
  /** Rows in this group, preserving caller order. */
  rows: Payout[];
  /** Sum of amount_minor across rows — surfaced in the group header. */
  totalMinor: bigint;
  /**
   * Earliest / latest created_at across the group's rows, as the raw ISO
   * strings the API returned.
   *
   * A batch is a CSV upload, so its rows almost always share a day — but
   * "almost always" is not "always": an upload that straddles midnight, or
   * a batch whose rows were re-initiated later, spans two. Carrying BOTH
   * ends lets the header say so instead of quietly picking one and
   * implying the batch happened on a day half its rows didn't.
   *
   * ISO-8601 UTC strings from the same source compare correctly with
   * lexicographic <, which is what the ordering below relies on.
   */
  firstCreatedAt: string;
  lastCreatedAt: string;
}

/** Ordering for batched groups. Orphans are pinned last either way. */
export type GroupSort = "oldest" | "newest";

function spanOf(rows: readonly Payout[]): {
  firstCreatedAt: string;
  lastCreatedAt: string;
} {
  let first = rows[0].created_at;
  let last = rows[0].created_at;
  for (const r of rows) {
    if (r.created_at < first) first = r.created_at;
    if (r.created_at > last) last = r.created_at;
  }
  return { firstCreatedAt: first, lastCreatedAt: last };
}

/**
 * groupByBatch partitions rows by batch_id while preserving caller
 * order within each group. Batched groups appear first, sorted by
 * their oldest row's created_at. The orphan group always lands last —
 * one-off rows are not the primary workload and shouldn't push CSV
 * batches off the top of the page.
 *
 * `sort` chooses the direction for the BATCHED groups only:
 *
 *   "oldest" (default) — CSV uploaded earlier surfaces first, matching
 *                        the "oldest pending first" queue model
 *                        /approvals is built around.
 *   "newest"           — most recent batch first, for /payouts, where
 *                        the page is a history rather than a work queue
 *                        and the useful end is today's activity.
 *
 * The orphan group is deliberately NOT re-ordered by the flag. It is a
 * bucket of unrelated single rows spanning the merchant's whole history,
 * so it has no meaningful position on a timeline; dropping it into the
 * middle of a date-sorted list would read as a sorting bug.
 *
 * Empty input → empty result (no synthetic orphan group).
 */
export function groupByBatch(
  rows: readonly Payout[],
  sort: GroupSort = "oldest",
  /**
   * collapseSingletons folds every batch that contains exactly ONE row into
   * the singles group, alongside rows that never had a batch at all.
   *
   * A one-row batch is a batch only in the database's opinion. To an operator
   * it is one payment, and rendering each as its own header — "Batch
   * batch_170 · 1 row · 1 success" — turns a page of thirty payments into
   * thirty cards that have to be expanded one at a time to see thirty single
   * amounts. The batch identity is still there when it matters (the row links
   * to its batch); it just stops being the organising principle for groups
   * that organise nothing.
   *
   * Off by default so /approvals, where every batch is a unit of work
   * regardless of size, is unaffected.
   */
  collapseSingletons = false,
): PayoutGroup[] {
  const batched = new Map<number, Payout[]>();
  const orphans: Payout[] = [];
  for (const r of rows) {
    if (r.batch_id == null) {
      orphans.push(r);
    } else {
      const existing = batched.get(r.batch_id);
      if (existing) existing.push(r);
      else batched.set(r.batch_id, [r]);
    }
  }

  // Second pass, deliberately: whether a batch is a singleton is only known
  // once every row has been placed, so this cannot be folded into the loop
  // above without assuming rows arrive grouped.
  if (collapseSingletons) {
    for (const [batchId, rs] of batched) {
      if (rs.length === 1) {
        orphans.push(rs[0]);
        batched.delete(batchId);
      }
    }
    // Re-sort the merged bucket by creation time. Without this, folded
    // singletons arrive in batch-id order after the true orphans and the
    // table reads as though it lost its ordering halfway down.
    orphans.sort((a, b) => {
      const cmp = a.created_at.localeCompare(b.created_at);
      return sort === "newest" ? -cmp : cmp;
    });
  }
  const sumMinor = (xs: Payout[]) =>
    xs.reduce((acc, p) => acc + p.amount_minor, 0n);
  const groups: PayoutGroup[] = Array.from(batched.entries())
    .map(([batchId, rs]) => ({
      key: `batch-${batchId}`,
      batchId,
      rows: rs,
      totalMinor: sumMinor(rs),
      ...spanOf(rs),
    }))
    .sort((a, b) => {
      const cmp = a.firstCreatedAt.localeCompare(b.firstCreatedAt);
      // Tie-break on batch id so batches uploaded in the same second
      // (bulk seeding, or a retried upload) keep a stable, reproducible
      // order instead of depending on Map insertion order.
      const tie = cmp !== 0 ? cmp : (a.batchId ?? 0) - (b.batchId ?? 0);
      return sort === "newest" ? -tie : tie;
    });
  if (orphans.length > 0) {
    groups.push({
      key: "orphans",
      batchId: null,
      rows: orphans,
      totalMinor: sumMinor(orphans),
      ...spanOf(orphans),
    });
  }
  return groups;
}

/**
 * countSingletonBatches reports how many one-row batches are in a row set.
 *
 * Drives the "group single-payout batches" checkbox label, so the operator
 * can see what turning it on would collapse before they turn it on — and so
 * the control can be hidden entirely when there is nothing to collapse
 * rather than sitting there doing nothing.
 */
export function countSingletonBatches(rows: readonly Payout[]): number {
  const sizes = new Map<number, number>();
  for (const r of rows) {
    if (r.batch_id == null) continue;
    sizes.set(r.batch_id, (sizes.get(r.batch_id) ?? 0) + 1);
  }
  let n = 0;
  for (const size of sizes.values()) if (size === 1) n++;
  return n;
}

/**
 * statusCounts tallies a group's rows by terminal/non-terminal status.
 * Used by the /payouts page's batch header to render a quick
 * distribution chip — "180 success · 15 pending · 5 failure" — without
 * the operator needing to expand the group.
 */
export interface StatusCounts {
  needsApproval: number;
  pending: number;
  success: number;
  failure: number;
}

export function statusCounts(rows: readonly Payout[]): StatusCounts {
  const c: StatusCounts = {
    needsApproval: 0,
    pending: 0,
    success: 0,
    failure: 0,
  };
  for (const r of rows) {
    switch (r.status) {
      case "needs_approval":
        c.needsApproval++;
        break;
      case "pending":
        c.pending++;
        break;
      case "success":
        c.success++;
        break;
      case "failure":
        c.failure++;
        break;
    }
  }
  return c;
}
